import { TreeItem } from "@/domain/common.interface";
import { RESULT_STATUS, VISIT_STATUS } from "../domain/beneficiary.enum";
import { EventResult } from "../domain/event.interface";
import { AppTheme } from "./theme";

export const ACCOUNT_ID = process.env.REACT_APP_ACCOUNT_ID as string;

export const a11yProps = (index: number) => {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
};

export const toBase64 = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = (error) => reject(error);
	});

export const getStatusColor = (result: string, theme: AppTheme) => {
	switch (result) {
		case RESULT_STATUS.POSITIVE:
			return theme.palette.error.light;
		case RESULT_STATUS.NEGATIVE:
			return theme.palette.secondary.light;
		case RESULT_STATUS.EXCUSED_ABSENCE:
			return theme.palette.outline.dark;
		case RESULT_STATUS.UNEXCUSED_ABSENCE:
			return theme.palette.warning.light;
		case VISIT_STATUS.MISSED:
			return theme.palette.outline.dark;
		default:
			return theme.palette.secondary.light;
	}
};

export const getStatusText = (result: string) => {
	switch (result) {
		case RESULT_STATUS.POSITIVE:
			return "Pos UDT";
		case RESULT_STATUS.NEGATIVE:
			return "Neg UDT";
		case RESULT_STATUS.EXCUSED_ABSENCE:
			return "Ex. Absence";
		case RESULT_STATUS.UNEXCUSED_ABSENCE:
			return "Un. Absence";
		case VISIT_STATUS.MISSED:
			return "Missed entry";
		default:
			return "Pos UDT";
	}
};

/**
 * Get the display color for a result with event result support (new approach with fallback to legacy)
 * @param result - The result ID (e.g., "POSITIVE", "NEGATIVE")
 * @param theme - The app theme
 * @param eventResults - Optional array of event results from the event configuration
 * @returns The color code
 */
export const getResultColor = (
	result: string | undefined,
	theme: AppTheme,
	eventResults?: EventResult[],
): string => {
	// Return default color if result is undefined
	if (!result) return theme.palette.secondary.light;

	// First, try to get color from event results (new approach)
	if (eventResults && eventResults.length > 0) {
		const eventResult = eventResults.find((er) => er.id === result);
		if (eventResult?.displayColor) {
			return eventResult.displayColor;
		}
	}

	// Fallback to legacy enum-based colors
	return getStatusColor(result, theme);
};

/**
 * Get the display text for a result with event result support (new approach with fallback to legacy)
 * @param result - The result ID (e.g., "POSITIVE", "NEGATIVE")
 * @param eventResults - Optional array of event results from the event configuration
 * @returns The display text
 */
export const getResultText = (
	result: string | undefined,
	eventResults?: EventResult[],
): string => {
	if (!result) return "";

	// First, try to get text from event results (new approach)
	if (eventResults && eventResults.length > 0) {
		const eventResult = eventResults.find((er) => er.id === result);
		if (eventResult) {
			// Use shortName if available, otherwise use display
			return eventResult.shortName || eventResult.display;
		}
	}

	// Fallback to legacy enum-based text
	return getStatusText(result);
};

/**
 * Check if a result is an "earned" result (valid visit) with event result support
 * @param result - The result ID (e.g., "POSITIVE", "NEGATIVE")
 * @param eventResults - Optional array of event results from the event configuration
 * @returns True if the result is earned/valid
 */
export const isResultEarned = (
	result: string | undefined,
	eventResults?: EventResult[],
): boolean => {
	if (!result) return false;

	// First, try to determine from event results (new approach)
	if (eventResults && eventResults.length > 0) {
		const eventResult = eventResults.find((er) => er.id === result);
		if (eventResult) {
			return eventResult.validResult === true;
		}
	}

	// Fallback to legacy enum-based logic
	return result === "NEGATIVE" || result === "POSITIVE";
};

/**
 * Check if a result is an "absent" result with event result support
 * @param result - The result ID (e.g., "EXCUSED_ABSENCE", "UNEXCUSED_ABSENCE")
 * @param eventResults - Optional array of event results from the event configuration
 * @returns True if the result is an absence
 */
export const isResultAbsent = (
	result: string | undefined,
	eventResults?: EventResult[],
): boolean => {
	if (!result) return false;

	// First, try to determine from event results (new approach)
	if (eventResults && eventResults.length > 0) {
		const eventResult = eventResults.find((er) => er.id === result);
		if (eventResult) {
			return eventResult.validResult === false;
		}
	}

	// Fallback to legacy enum-based logic
	return result === "EXCUSED_ABSENCE" || result === "UNEXCUSED_ABSENCE";
};

export const treeToArray = (tree: TreeItem[]): TreeItem[] => {
	const result: TreeItem[] = [];
	for (const item of tree) {
		result.push(item);
		result.push(...treeToArray(item.children));
	}
	return result;
};

export const getLeafNodes = (tree: TreeItem[]): TreeItem[] => {
	const result: TreeItem[] = [];
	for (const item of tree) {
		if (item.children.length === 0) {
			// This is a leaf node
			result.push(item);
		} else {
			// This has children, get leaf nodes from children
			result.push(...getLeafNodes(item.children));
		}
	}
	return result;
};

export const formatCommaSeparatedNumber = (
	value: number | undefined,
): string => {
	if (value === undefined) return "0.00";
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

export const generateSampleFromRegex = (regex: string): string => {
	// Remove leading ^ and trailing $ if they exist
	const cleanedRegex = regex.replace(/^\^/, "").replace(/\$$/, "");
	let sample = "";
	let i = 0;

	while (i < cleanedRegex.length) {
		let char = cleanedRegex[i];

		if (char === "\\") {
			// Handle escape sequences (e.g., \d, \w)
			i++; // Skip the backslash
			char = cleanedRegex[i];

			if (char === "d") {
				// Handle digit placeholder
				sample += Math.floor(Math.random() * 10);
			} else if (char === "w") {
				// Handle word character placeholder
				const letters =
					"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
				sample += letters.charAt(Math.floor(Math.random() * letters.length));
			} else if (char === "s") {
				// Handle whitespace character placeholder
				sample += " ";
			} else {
				// Handle other escaped characters
				sample += char;
			}
		} else if (char === "{") {
			// Handle quantifiers (e.g., {5,6})
			const endBracketIndex = cleanedRegex.indexOf("}", i);
			if (endBracketIndex > -1) {
				const quantifier = cleanedRegex.slice(i + 1, endBracketIndex);
				const [min, max] = quantifier.split(",").map(Number);
				const repeatCount = max || min; // Use the max if specified, otherwise min

				const lastChar = sample[sample.length - 1];
				sample = sample.slice(0, -1); // Remove the last character (it's already counted)

				for (let j = 0; j < repeatCount; j++) {
					sample += lastChar;
				}

				i = endBracketIndex; // Move index to the end of the quantifier
			}
		} else {
			// Handle custom character classes or specific characters
			sample += char;
		}
		i++;
	}

	return sample;
};

export const generateCINFromFormat = (cinFormat: string): string | never => {
	// If no format is provided, generate a random 8-digit number
	if (!cinFormat) {
		return Math.floor(10000000 + Math.random() * 90000000).toString();
	}

	// Remove leading ^ and trailing $ if they exist
	const cleanedRegex = cinFormat.replace(/^\^/, "").replace(/\$$/, "");
	let cin = "";
	let i = 0;

	while (i < cleanedRegex.length && cin.length < 8) {
		let char = cleanedRegex[i];

		if (char === "\\") {
			// Handle escape sequences
			i++; // Skip the backslash
			char = cleanedRegex[i];

			if (char === "d") {
				// Generate a random digit
				cin += Math.floor(Math.random() * 10);
			} else if (char === "w") {
				// Generate a random word character (letter or digit)
				const wordChars =
					"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
				cin += wordChars.charAt(Math.floor(Math.random() * wordChars.length));
			} else if (char === "s") {
				// Skip whitespace in CIN generation
				// cin += " ";
			} else {
				// Handle other escaped characters
				cin += char;
			}
		} else if (char === "[") {
			// Handle character classes like [A-Z], [0-9], etc.
			const endBracketIndex = cleanedRegex.indexOf("]", i);
			if (endBracketIndex > -1) {
				const charClass = cleanedRegex.slice(i + 1, endBracketIndex);
				cin += generateCharFromClass(charClass);
				i = endBracketIndex; // Move index to the end of the character class
			}
		} else if (char === "{") {
			// Handle quantifiers like {3}, {2,4}
			const endBracketIndex = cleanedRegex.indexOf("}", i);
			if (endBracketIndex > -1) {
				const quantifier = cleanedRegex.slice(i + 1, endBracketIndex);
				const [min, max] = quantifier.split(",").map(Number);
				const repeatCount = max
					? Math.floor(Math.random() * (max - min + 1)) + min
					: min;

				// Get the previous character pattern to repeat
				const prevChar = cleanedRegex[i - 1];
				if (prevChar === "d" && cleanedRegex[i - 2] === "\\") {
					// Repeat digits
					for (let j = 0; j < repeatCount - 1 && cin.length < 8; j++) {
						cin += Math.floor(Math.random() * 10);
					}
				} else if (prevChar === "]") {
					// Find the corresponding opening bracket
					let openBracketIndex = i - 1;
					let bracketCount = 1;
					while (openBracketIndex >= 0 && bracketCount > 0) {
						openBracketIndex--;
						if (cleanedRegex[openBracketIndex] === "]") bracketCount++;
						if (cleanedRegex[openBracketIndex] === "[") bracketCount--;
					}
					if (openBracketIndex >= 0) {
						const charClass = cleanedRegex.slice(openBracketIndex + 1, i - 1);
						for (let j = 0; j < repeatCount - 1 && cin.length < 8; j++) {
							cin += generateCharFromClass(charClass);
						}
					}
				}

				i = endBracketIndex; // Move index to the end of the quantifier
			}
		} else if (char === "+" || char === "*" || char === "?") {
			// Handle other quantifiers - for simplicity, treat + as {1,3}, * as {0,2}, ? as {0,1}
			const prevChar = cleanedRegex[i - 1];
			const repeatCount =
				char === "+"
					? Math.floor(Math.random() * 3) + 1
					: char === "*"
						? Math.floor(Math.random() * 3)
						: Math.floor(Math.random() * 2);

			if (prevChar === "d" && cleanedRegex[i - 2] === "\\") {
				for (let j = 0; j < repeatCount && cin.length < 8; j++) {
					cin += Math.floor(Math.random() * 10);
				}
			}
		} else if (char !== "^" && char !== "$") {
			// Handle literal characters (skip anchors)
			cin += char;
		}

		i++;
	}

	// Ensure we have exactly 8 characters, pad with random digits if needed
	while (cin.length < 8) {
		cin += Math.floor(Math.random() * 10);
	}

	// Trim to exactly 8 characters if longer
	return cin.substring(0, 8);
};

// Helper function to generate a character from a character class
const generateCharFromClass = (charClass: string): string => {
	if (charClass.includes("-")) {
		// Handle ranges like A-Z, 0-9, a-z
		const ranges = charClass.split("");
		let chars = "";

		for (let i = 0; i < ranges.length; i++) {
			if (ranges[i + 1] === "-" && ranges[i + 2]) {
				// This is a range
				const start = ranges[i].charCodeAt(0);
				const end = ranges[i + 2].charCodeAt(0);
				for (let code = start; code <= end; code++) {
					chars += String.fromCharCode(code);
				}
				i += 2; // Skip the dash and end character
			} else if (ranges[i] !== "-") {
				chars += ranges[i];
			}
		}

		return chars.charAt(Math.floor(Math.random() * chars.length));
	} else {
		// Handle explicit character lists
		return charClass.charAt(Math.floor(Math.random() * charClass.length));
	}
};

export const parsePeriodLengthText = (periodLength: number) => {
	switch (periodLength) {
		case 1:
			return " weekly for";
		case 4.348:
			return " monthly for";
		case 2:
			return " bi-weekly for";
		default:
			return ` ${periodLength} weeks for`;
	}
};
